import React, { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import { externalTooltipHandler } from '../ExternalTooltipHandler';
import { Row, Col, Space, Tooltip } from 'antd';
import styled from 'styled-components';
import LegendLabel from './LegendLabel';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { useGraphDataScaler } from '../Hooks/useGraphDataScaler';
import InfoPopover from 'modules/Common/Components/InfoPopover';

interface ICustomChartProps {
  title?: any;
  boxRightOffset?: any;
  boxBottomOffset?: any;
  annotationHeight?: any;
  campaignId?: any;
  emailId?: any;
  tooltip?: string;
}
const gradientColor = [
  ['rgba(255, 150, 45, 0.5)', 'rgba(255, 150, 45, 0.1)'],
  ['rgba(139, 165, 230, 0.7)', 'rgba(139, 165, 230, 0.3)'],
  ['rgba(211, 27, 27, 0.5)', 'rgba(211, 27, 27, 0.1)'],
  ['rgba(24, 195, 81, 0.7)', 'rgba(24, 195, 81, 0.3)'],
];
let gradients: any = [];

const CustomChart: React.FC<ICustomChartProps> = React.memo((props: ICustomChartProps) => {
  const canvasRef: any = useRef('');
  const [graphData, isTodayDateInData, setColorGradients]: any = useGraphDataScaler(gradients);

  const AllCampaignsFilterCheck = useSelector((state: RootState) => state.analyticsDashboard.appliedCampaignsFilters.isAllCampaigns);

  useEffect(() => {
    if (canvasRef && canvasRef.current && graphData && graphData?.pDatasets?.length > 0 && graphData?.xAxesDateLabels?.length > 0) {
      const someChart = new Chart(canvasRef.current, {
        type: 'line',
        data: {
          labels: graphData.xAxesDateLabels,
          datasets: graphData.pDatasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          elements: {
            line: {
              tension: 0.5,
            },
          },
          plugins: {
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: externalTooltipHandler,
            },
            legend: {
              display: true,
              position: 'top',
              align: 'start',
              textDirection: 'ltr',
              title: {
                display: true,
                text: ' ',
                position: 'start',
                font: {
                  size: 18,
                },
                padding: {
                  top: 10,
                  bottom: 10,
                },
              },
              labels: {
                usePointStyle: true,
                textAlign: 'center',
                generateLabels: function (chart) {
                  return [];
                },
              },
            },
          },
          scales: {
            xAxes: {
              ticks: {
                callback: function (label) {
                  return `${graphData.xAxesDateLabels[label].split('/').slice(0, 2).join('/')}`;
                },
              },
              grid: {
                display: false,
                borderWidth: 0,
              },
            },
            yAxes: {
              display: false,
              beginAtZero: true,
            },
          },
        },
      });
      return () => {
        someChart.destroy();
      };
    }
  }, [graphData]);

  useEffect(() => {
    gradients = gradientColor.map((colorArr) => {
      let gradient: any;
      gradient = canvasRef.current.getContext('2d').createLinearGradient(10, 400, 0, 0);
      gradient.addColorStop(0, colorArr[1]);
      // gradient.addColorStop(0.6, colorArr[0]);
      gradient.addColorStop(1, colorArr[0]);
      return gradient;
    });
    setColorGradients(gradients);
  }, [canvasRef.current]);

  return (
    <CanvasContainer>
      <canvas ref={canvasRef} id="myChart" height={480}></canvas>
      {graphData?.xAxesDateLabels?.length > 0 && isTodayDateInData && (
        <BoxAnnotation
          annotationHeight={props.annotationHeight ? props.annotationHeight : '75%'}
          bottomOffset={props?.boxBottomOffset ? props?.boxBottomOffset : '8%'}
          rightOffset={props?.boxRightOffset ? props?.boxRightOffset : '3%'}
        ></BoxAnnotation>
      )}

      <div className="custom-legend">
        {/* <h1>{JSON.stringify(graphData)}</h1> */}
        <Row className="align-items-center">
          {/* {graphData?.xAxesDateLabels?.length > 0 && ( */}
          <>
            {' '}
            <Col span={8}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className="chart-title">{props?.title}</span>
                <span style={{ display: 'flex', marginLeft: '8px' }}>
                  {props?.tooltip ? <InfoPopover placement="bottomLeft" infoText={props?.tooltip} width={341} /> : <></>}
                </span>
              </div>
            </Col>
            {!AllCampaignsFilterCheck && graphData?.legendLabels && graphData?.legendLabels[0] && (
              <Col span={15} className="legend-labels-container">
                <div className="d-flex justify-content-end">
                  <Space data-legend="legend-labels-wrapper">
                    {graphData?.legendLabels?.map((label: any, index: any) => {
                      return <LegendLabel key={`legend-label-${index}-${label}`} label={label} index={index} />;
                    })}
                  </Space>
                </div>
              </Col>
            )}
          </>
          {graphData?.xAxesDateLabels?.length === 0 && (
            <Col span={24}>
              <div className="d-flex justify-content-center align-items-center empty-graph">
                <h2>
                  Your open and click stats will be shown
                  <br />
                  here
                </h2>
              </div>
            </Col>
          )}

          {/* Show only if graph is empty */}
        </Row>
      </div>
    </CanvasContainer>
  );
});

export default CustomChart;
interface ICanvasContainerProps {
  backgroundImage?: any;
  backgroundSize?: any;
}
const CanvasContainer = styled('div') <ICanvasContainerProps>`
  height: 100% !important;
  background-color: white;
  border-radius: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 10px;
  padding-left: 10px;
  padding-right: 10px;
  position: relative;
  cursor: pointer;
  .empty-graph {
    min-height: 400px;
    max-height: 400px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      min-height: 250px;
      max-height: 250px;
    }
    h2 {
      color: #cacaca;
      font-size: 23px;
      font-weight: 400;
      font-family: 'Roboto Slab', serif;
      line-height: 32px;
      letter-spacing: -0.5px;
      margin-bottom: 100px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 16px;
        line-height: 23px;
        margin-bottom: 40px;
      }
    }
  }
  canvas {
    background-color: white;
    width: 100%;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 480px;
    min-height: 480px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: 300px;
      min-height: 300px;
    }
  }
  .annotation {
    position: absolute;
    background-color: #cae0f3;
    border-radius: 6.5px;
    width: 13px;
    height: 75%;
    opacity: 0.74;
    right: 3%;
    bottom: 8%;
  }
  .custom-legend {
    position: absolute;
    width: 100%;
    top: 25px;
    left: 10px;
  }
  .legend-label {
    margin-top: 7px;
    max-width: 132px;
  }
  .legend-label-text {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    font-size: 10px;
    font-weight: 700;
    color: #424b50;
    margin: 0px;
    white-space: pre-wrap;
    text-overflow: ellipsis;
    word-break: break-all;
    overflow: hidden;
  }
  .legend-labels-container {
    justify-content: flex-end;
    align-items: center;
  }
  .chart-title {
    font-family: 'Roboto Slab';
    font-size: 28px;
    line-height: 47px;
    font-weight: 700;
    margin-left: 25px;
    color: #424b50;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
      line-height: 40px;
    }
  }
  .no-stats {
    font-weight: bold;
    font-size: x-large;
    margin-top: 170px;
    margin-left: -30px;
    color: darkgray;
  }
`;
interface IBoxAnnotationProps {
  bottomOffset?: string;
  rightOffset?: string;
  annotationHeight?: string;
}
const BoxAnnotation = styled('div') <IBoxAnnotationProps>`
  position: absolute;
  background-color: #cae0f3;
  border-radius: 6.5px;
  width: 13px;
  height: ${(props) => props.annotationHeight};
  opacity: 0.74;
  right: ${(props) => props.rightOffset};
  bottom: ${(props) => props.bottomOffset};
`;
